/**
 * Grammatical-form labels (ADR-111 D2).
 *
 * The backend (services/word_form.py) and the seeds store these labels in
 * English, from a closed set. Each interface locale translates them through its
 * own map, `locales/<code>/formLabels.ts`, selected by the interface locale, not
 * by the reader's native language. A value missing from the map, or a locale
 * whose map is empty, shows the stored English value, never a blank.
 */

/**
 * The closed set produced by backend/app/services/word_form.py (22 values), plus the
 * text gap forms (ADR-115 Implementation note 3): the adverb forms and the verb's
 * past participle, which a learner can pick for a gap and the classifier never assigns.
 */
export type FormLabel =
  | 'noun, plural'
  | 'noun, singular'
  | 'noun, irregular form'
  | 'noun'
  | 'verb, past tense'
  | 'verb, -ing form'
  | 'verb, 3rd-person singular'
  | 'verb, infinitive'
  | 'verb, irregular form'
  | 'verb, base form'
  | 'verb, past participle'
  | 'adjective, comparative'
  | 'adjective, superlative'
  | 'adjective, base form'
  | 'adjective, irregular form'
  | 'adverb'
  | 'adverb, base form'
  | 'adverb, comparative'
  | 'adverb, superlative'
  | 'preposition'
  | 'conjunction'
  | 'determiner'
  | 'pronoun'
  | 'interjection'
  | 'fixed expression'
  | 'phrasal verb'

export type FormLabelMap = Partial<Record<FormLabel, string>>

/** The lookup a table exposes as `learn.formLabel`: the locale's label, else the
 *  stored English value (for an unmapped or future free-text label). */
export function formLabelFrom(map: FormLabelMap): (raw: string) => string {
  return (raw: string): string => map[raw as FormLabel] || raw
}
